import { default as cx } from "clsx";
import { html } from "htm/preact";
import { createContext } from "preact";
import { useContext, useMemo } from "preact/hooks";

import { type Nullable } from "../types/types";

import * as Bible from "../modules/bible";
import { Progress } from "../modules/progress";

import { BibleContentChapter } from "./BibleContentChapter";
import { BibleContentItem } from "./BibleContentItem";
import { BibleContentTable } from "./BibleContentTable";
import { BibleContentLink } from "./BibleContentLink";
import { BibleContentVerse } from "./BibleContentVerse";
import { BibleContentVerseImage } from "./BibleContentVerseImage";
import { BibleTranslationInfo } from "./BibleTranslationInfo";

export type BibleContentSettings = {
  showChapters: boolean;
  showChapterRelatedResource: boolean;
  showHeadings: boolean;
  showLinks: boolean;
  showNarrativeImages: boolean;
  showUnitImages: boolean;
  showVerseNumbers: boolean;
  showVerseRelatedResources: boolean;
};

export const defaultSettings: BibleContentSettings = {
  showChapters: false,
  showChapterRelatedResource: false,
  showHeadings: false,
  showLinks: false,
  showNarrativeImages: false,
  showUnitImages: false,
  showVerseNumbers: false,
  showVerseRelatedResources: false,
};

export type BibleContentProps = {
  content: Bible.Content;
  progress?: Progress;
  settings?: Partial<BibleContentSettings>;
  translation: Nullable<Bible.TranslationPayload>;
};

export const BibleContent = (props: BibleContentProps) => {
  const settings = useMemo(
    () => ({ ...defaultSettings, ...props.settings }),
    [props.settings],
  );
  const className = useMemo(
    () =>
      cx("BibleContent", {
        "BibleContent--show-headings": settings.showHeadings,
        "BibleContent--show-verse-numbers": settings.showVerseNumbers,
      }),
    [settings],
  );
  const children = useMemo(
    () =>
      props.content.items.map((item) => {
        if (item instanceof Bible.ContentChapter) {
          return html`<${BibleContentChapter}
            chapter=${item}
            key=${item.key}
          />`;
        }
        if (item instanceof Bible.ContentTable) {
          return html`<${BibleContentTable}
            key=${item.key}
            table=${item}
            part="bible-content-table"
          />`;
        }
        if (item instanceof Bible.ContentParagraph) {
          const paragraph = item;
          return html`<p
            class=${cx("BibleContentParagraph", {
              "BibleContentParagraph--is-heading": paragraph.item.isHeading,
            })}
            data-style=${paragraph.item.styleAttribute}
            key=${paragraph.key}
            part=${`${
              paragraph.item.isHeading
                ? `bible-content-first-paragraph`
                : `bible-content-paragraph`
            }`}
          >
            ${paragraph.items.map((item) => {
              if (item instanceof Bible.ContentLink) {
                return html`<${BibleContentLink}
                  link=${item}
                  key=${item.key}
                  part="bible-content-link"
                />`;
              }
              if (item instanceof Bible.ContentVerse) {
                return html`<${BibleContentVerse}
                  key=${item.key}
                  verse=${item}
                  part="bible-content-verse"
                />`;
              }
              if (item instanceof Bible.ContentVerseImage) {
                return html`<${BibleContentVerseImage}
                  image=${item}
                  key=${item.key}
                  part="bible-content-verse-image"
                />`;
              }
              if (item instanceof Bible.ContentItem) {
                return html`<${BibleContentItem}
                  item=${item}
                  key=${item.key}
                  part="bible-content-item"
                />`;
              } /* c8 ignore next */
              return null;
            })}
          </p>`;
        } /* c8 ignore next */
        return null;
      }),
    [props.content],
  );
  return html`
    <div
      class=${className}
      data-query=${props.content.query}
      data-translation=${props.content.translation}
      part="bible-content"
    >
      <${BibleSettingsContext.Provider} value=${settings}>
        <div
          children=${children}
          part="bible-content-text"
          class="BibleContent__text"
        />
      <//>
      ${props.translation
        ? html`<${BibleTranslationInfo} translation=${props.translation} />`
        : null}
    </div>
  `;
};

/* Bible Progress */

export const BibleProgressContext = createContext<Progress>(new Progress());

export function useBibleProgress() {
  return useContext(BibleProgressContext);
}

/* Bible Settings */

export const BibleSettingsContext =
  createContext<BibleContentSettings>(defaultSettings);

export function useBibleSettings() {
  return useContext(BibleSettingsContext);
}
